/**
 * Draws at most ~60 frames a second on a high-refresh display (2026-10-15,
 * QA #9: "GPU at 94%" — on an RTX 3070 *laptop*, whose panel is 144-165Hz).
 *
 * `requestAnimationFrame` fires at the display's refresh rate, and the frame
 * loop rendered the full post-processed scene on every callback: on a 165Hz
 * panel that is 2.75x the work of the 60fps the game is designed and tuned
 * for, for motion nobody asked for, and it is what pins a GPU near 100%.
 *
 * Renders every k-th callback, where k is the whole number of refresh
 * intervals that fits in a 60fps frame — so pacing stays even (144Hz -> 72fps,
 * 165Hz -> 82fps, 240Hz -> 60fps, 120Hz -> 60fps, 60Hz -> every frame) instead
 * of the 2-then-3 judder a time-based cap produces at non-multiples.
 *
 * ## The refresh estimate (amended 2026-10-16, QA 10.10.2026 #9)
 *
 * It used to be the *shortest* recent callback gap, on the reasoning that
 * nothing makes a callback arrive early. Something does: an unthrottled or
 * busy browser delivers callbacks in bursts, a couple of milliseconds apart
 * after a long frame. One 4ms gap in the window made k = 4, and a page whose
 * rAF actually ran at 71/s drew 17.8 frames a second — which the adaptive
 * resolution then read as a slow GPU and never recovered from.
 *
 * Now it is the **median** gap over the window, which a burst cannot move,
 * and k is additionally capped so the drawn rate never falls below
 * `MIN_RENDER_HZ` while callbacks arrive faster than that: skipping exists to
 * shed frames *above* 60, never to make a display that delivers 70 draw 35.
 * Gaps under 3ms are still ignored outright.
 */
export const TARGET_FRAME_MS = 1000 / 60;
const WINDOW = 60;
const MIN_PLAUSIBLE_REFRESH_MS = 3;
/** Pacing never draws fewer frames than this while rAF delivers more. */
export const MIN_RENDER_HZ = 55;
/** Re-sort the window this often, not on every callback. */
const ESTIMATE_EVERY = 15;

export class RenderPacer {
  private readonly gaps = new Float64Array(WINDOW);
  private gapIndex = 0;
  private gapCount = 0;
  private last = -1;
  private sinceRender = 0;
  private readonly sorted = new Float64Array(WINDOW);
  private sinceEstimate = 0;
  private cachedNth = 1;

  constructor(private readonly enabled = true) {}

  /** Call at the top of every rAF callback; false means skip drawing this one. */
  shouldRender(nowMs: number): boolean {
    if (this.last >= 0) {
      const gap = nowMs - this.last;
      if (gap >= MIN_PLAUSIBLE_REFRESH_MS) {
        this.gaps[this.gapIndex] = gap;
        this.gapIndex = (this.gapIndex + 1) % WINDOW;
        if (this.gapCount < WINDOW) this.gapCount++;
        if (++this.sinceEstimate >= ESTIMATE_EVERY || this.gapCount === 10) {
          this.sinceEstimate = 0;
          this.cachedNth = this.estimateNth();
        }
      }
    }
    this.last = nowMs;
    if (!this.enabled) return true;
    this.sinceRender++;
    if (this.sinceRender >= this.everyNth) {
      this.sinceRender = 0;
      return true;
    }
    return false;
  }

  /** How many callbacks per rendered frame, from the current refresh estimate. */
  get everyNth(): number {
    return this.gapCount < 10 ? 1 : this.cachedNth;
  }

  /** Median recent callback gap, in ms; 0 before there is enough to say. */
  get refreshEstimateMs(): number {
    if (this.gapCount < 10) return 0;
    const n = this.gapCount;
    const view = this.sorted.subarray(0, n);
    view.set(this.gaps.subarray(0, n));
    view.sort();
    return n % 2 ? view[(n - 1) / 2] : (view[n / 2 - 1] + view[n / 2]) / 2;
  }

  private estimateNth(): number {
    const refresh = this.refreshEstimateMs;
    if (refresh <= 0) return 1;
    const byTarget = Math.floor(TARGET_FRAME_MS / refresh + 0.05);
    // Never pace below MIN_RENDER_HZ when callbacks arrive faster than it.
    const byFloor = Math.floor(1000 / refresh / MIN_RENDER_HZ);
    return Math.max(1, Math.min(byTarget, byFloor));
  }
}
